// Per-user favourite projects — stored in localStorage keyed by user
// id so different signed-in users on the same browser can't see each
// other's state. Planned to migrate to the backend user preferences
// table alongside the other 77+ prefs.
//
// Favourites are a per-user concept, not per-workspace: starring a
// project adds it to that user's favourites regardless of which
// workspace the project lives in. Rendering per-workspace is handled
// at the display layer (filter the workspace's projects by favId set).

export const favKey = (userId) => `pidly.favouriteProjects.${userId || 'anon'}`;

function readSet(key) {
  try {
    const raw = localStorage.getItem(key);
    return new Set(raw ? JSON.parse(raw) : []);
  } catch { return new Set(); }
}

/** Fired whenever favourites change, so every list showing them can
 *  re-read. The `storage` event does NOT fire in the document that did
 *  the writing, so without this the project page and the left dock —
 *  two components reading the same key in the same tab — disagreed
 *  until a reload. */
export const FAV_EVENT = 'pidly:favourites-changed';

function announce() {
  try { window.dispatchEvent(new Event(FAV_EVENT)); } catch { /* no window */ }
}

function writeSet(key, set) {
  try { localStorage.setItem(key, JSON.stringify([...set])); } catch { /* private mode */ }
  announce();
}

export function toggleFav(userId, projectId) {
  if (!projectId) return;
  const key = favKey(userId);
  const set = readSet(key);
  if (set.has(projectId)) set.delete(projectId); else set.add(projectId);
  writeSet(key, set);
}

export function isFav(userId, projectId) {
  return readSet(favKey(userId)).has(projectId);
}

// ── Recently-opened workspaces ─────────────────────────────────────────
// Per-user, most-recent-first list of workspace (project) ids the user has
// opened from the list. Same per-user keying rationale as favourites.
export const recentKey = (userId) => `pidly.recentWorkspaces.${userId || 'anon'}`;
const RECENT_MAX = 15;

export function readRecentWorkspaces(userId) {
  try {
    const raw = localStorage.getItem(recentKey(userId));
    const list = raw ? JSON.parse(raw) : [];
    return Array.isArray(list) ? list : [];
  } catch { return []; }
}

export function recordRecentWorkspace(userId, projectId) {
  if (!projectId) return;
  const key = recentKey(userId);
  const list = readRecentWorkspaces(userId).filter(e => e && e.id !== projectId);
  list.unshift({ id: projectId, at: Date.now() });
  try { localStorage.setItem(key, JSON.stringify(list.slice(0, RECENT_MAX))); } catch {}
}
